import React, { type ReactElement, useEffect, type ReactNode, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Text, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';

import {
	AdControlProvider,
	AdControlDropdownMenu,
	DismissalButton,
	ReasonsButton,
} from '@atlassian/ad-control-toolkit';

import type { EntryPointId } from '../LoomCrossSellPopupCard/BaseLoomPopupCard.tsx';
import { UNSAFE_expValNoExposure } from '@confluence/feature-experiments';

export const DATA_PRIVACY_LINK = 'https://www.atlassian.com/legal/privacy-policy';

export const loomAdControlI18n = defineMessages({
	dismissFlagTitle: {
		id: 'loom-utils.loom-ad-control.dismiss-flag-title',
		defaultMessage: 'Got it, thanks for letting us know',
		description:
			'Title of the flag that shows up when a user dismisses one of the loom videos cross-flow touchpoints.',
	},
	dismissFlagDescription: {
		id: 'loom-utils.loom-ad-control.dismiss-flag-description',
		defaultMessage: 'You won’t see this here as often.',
		description:
			'Description of the flag that shows up when a user dismisses one of the loom videos cross-flow touchpoints.',
	},
	reasonsModalTitle: {
		id: 'loom-utils.loom-ad-control.reasons-modal-title',
		defaultMessage: 'Why am I seeing this?',
		description:
			'Title of the reasons modal explaining why the loom cross-flow touchpoint is being shown',
	},
	reasonsModalExplanation: {
		id: 'loom-utils.loom-ad-control.reasons-modal-explanation',
		defaultMessage:
			'We think that <strong>Loom</strong> will improve your team’s productivity. The recommendation is based on:',
		description: 'Explanation as to why the loom cross-flow touchpoint is being shown',
	},
	reasonsModalBulletOne: {
		id: 'loom-utils.loom-ad-control.reasons-modal-bullet-one',
		defaultMessage: 'the size of your team',
		description: 'First bullet point of the reasons modal for loom cross-flow touchpoint ad',
	},
	reasonsModalBulletTwo: {
		id: 'loom-utils.loom-ad-control.reasons-modal-bullet-two',
		defaultMessage: 'your existing Atlassian products',
		description: 'Second bullet point of the reasons modal for loom cross-flow touchpoint ad',
	},
	privacyPolicy: {
		id: 'loom-utils.loom-ad-control.privacy-policy',
		defaultMessage: 'Learn more about your data privacy with Atlassian',
		description: "Link to Atlassian's privacy policy",
	},
});

const linkStyles = xcss({
	marginTop: 'space.150',
});

const moreIconStyles = xcss({
	alignSelf: 'center',
});

type LoomAdControlReasonModalBodyProps = {
	// entrypoint id of the entrypoint the popup is triggered from
	entryPointId: EntryPointId;
};

const LoomAdControlReasonModalBody: React.FC<LoomAdControlReasonModalBodyProps> = ({
	entryPointId,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'loomAdControlReasonsModal',
				attributes: {
					adType: 'crossFlow',
					entrypoint: entryPointId,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, entryPointId]);

	return (
		<>
			<Text color="color.text">
				{formatMessage(loomAdControlI18n.reasonsModalExplanation, {
					strong: (chunks: ReactNode[]) => (
						<Text weight="bold" color="color.text">
							{chunks}
						</Text>
					),
				})}
			</Text>
			<ul>
				<li>{formatMessage(loomAdControlI18n.reasonsModalBulletOne)}</li>
				<li>{formatMessage(loomAdControlI18n.reasonsModalBulletTwo)}</li>
			</ul>
			<Box xcss={linkStyles}>
				<Anchor href={DATA_PRIVACY_LINK} target="_blank">
					{formatMessage(loomAdControlI18n.privacyPolicy)}
				</Anchor>
			</Box>
		</>
	);
};

type TriggerProps = {
	testId: string;
	triggerRef: React.Ref<any>;
	onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
};

type AdControlProps = {
	onDismiss: () => Promise<void>;
	// entrypoint id of the entrypoint the popup is triggered from
	entryPointId: EntryPointId;
	spacing?: 'compact' | 'default';
	customTrigger?: (props: TriggerProps) => ReactElement;
};

export const LoomAdControl: React.FC<AdControlProps> = ({
	onDismiss,
	entryPointId,
	spacing = 'compact',
	customTrigger,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const fireTriggerClickAnalyticsEvent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'toggleLoomAdControlDropdown',
				source: 'loomCrossFlowPopupCard',
				attributes: {
					adType: 'crossFlow',
					entrypoint: entryPointId,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, entryPointId]);

	return (
		<AdControlProvider>
			<AdControlDropdownMenu
				itemId="loom-ad-controls"
				{...(UNSAFE_expValNoExposure(
					'migrate_confluence_loom_xflow_popup_to_spotlight',
					'isEnabled',
					false,
				)
					? { placement: 'top-end' } // align the menu to be on top so it doesn't cover the spotlight
					: {})}
				trigger={({ triggerRef, ...props }) =>
					customTrigger ? (
						customTrigger({
							testId: 'loom-utils.loom-ad-control.custom-button',
							triggerRef,
							onClick: (e) => {
								fireTriggerClickAnalyticsEvent();
								props.onClick?.(e);
							},
						})
					) : (
						<Box xcss={moreIconStyles}>
							<IconButton
								{...props}
								onClick={(e) => {
									fireTriggerClickAnalyticsEvent();
									props.onClick?.(e);
								}}
								appearance="subtle"
								spacing={spacing}
								icon={ShowMoreHorizontalIcon}
								ref={triggerRef}
								label="LoomAdControl"
								testId="loom-utils.loom-ad-control.button"
							/>
						</Box>
					)
				}
				{...(UNSAFE_expValNoExposure(
					'migrate_confluence_loom_xflow_popup_to_spotlight',
					'isEnabled',
					false,
				)
					? { strategy: 'absolute' }
					: {})}
			>
				{(props) => (
					<>
						<DismissalButton
							{...props}
							flagProps={{
								title: formatMessage(loomAdControlI18n.dismissFlagTitle),
								description: formatMessage(loomAdControlI18n.dismissFlagDescription),
							}}
							onClick={onDismiss}
						/>
						<ReasonsButton
							{...props}
							onClick={() => {
								createAnalyticsEvent({
									type: 'sendUIEvent',
									data: {
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'toggleLoomAdControlReasonsModal',
										source: 'loomAdControlDropdown',
										attributes: {
											adType: 'crossFlow',
											entrypoint: entryPointId,
										},
									},
								}).fire();
							}}
							modalProps={{
								title: formatMessage(loomAdControlI18n.reasonsModalTitle),
								body: <LoomAdControlReasonModalBody entryPointId={entryPointId} />,
							}}
						/>
					</>
				)}
			</AdControlDropdownMenu>
		</AdControlProvider>
	);
};
